import {
  LoadingSkeleton,
  Text,
  //TextButton,
  typography,
} from '@joor/design-system'
import styled from 'styled-components/macro'

import {
  Maybe,
  OrderBillingAddress,
  OrderShippingAddress,
  OrderStatus,
} from '__generated__/atlas-types'

import { EMPTY_CELL_CONTENT } from 'shop/products/utils'

import { messages } from '../../constants/messages'
import { formatOrderAddress } from 'features/Orders/Review/utils/addresses.utils'
import { DetailsListItem } from 'features/Orders/components'

export const OrderShippingBillingInfo = ({
  loading,
  shippingAddress,
  billingAddress,
  shippingMethodName,
  doorWithCode,
  trackingNumber,
  orderStatus,
  paymentMethodName,
  showShippingCode,
  orderTaxLabel,
  orderTaxNumber,
  orderBillingCode,
}: {
  loading?: boolean
  shippingAddress?: OrderShippingAddress
  billingAddress?: OrderBillingAddress
  shippingMethodName?: string
  doorWithCode?: string
  trackingNumber?: string | null
  orderStatus?: OrderStatus
  paymentMethodName?: string | null
  showShippingCode?: boolean
  orderTaxLabel?: string | null
  orderTaxNumber?: string | null
  orderBillingCode?: string | null
}) => {
  return (
    <Container>
      {loading ? (
        <>
          <InfoItemSkeleton />
          <InfoItemSkeleton />
        </>
      ) : (
        <>
          <InfoItem
            titleLabel={messages.shippingInfo}
            addressText={formatOrderAddress(shippingAddress)}
            company={shippingAddress?.company}
            phoneNumber={shippingAddress?.phone}
            email={shippingAddress?.email}
            renderRightInfo={() => (
              <>
                {showShippingCode && (
                  <DetailsListItem
                    title={messages.shippingCode}
                    content={
                      shippingAddress?.sourceShippingAddress?.code ??
                      EMPTY_CELL_CONTENT
                    }
                  />
                )}
                <DetailsListItem
                  title={messages.shippingMethod}
                  content={shippingMethodName ?? EMPTY_CELL_CONTENT}
                />
                {orderStatus === OrderStatus.SHIPPED && (
                  <DetailsListItem
                    title={messages.trackingNumber}
                    content={
                      !trackingNumber || trackingNumber === ' '
                        ? EMPTY_CELL_CONTENT
                        : trackingNumber
                    }
                  />
                )}
              </>
            )}
            renderLeftInfo={() => (
              <DetailsListItem
                title={messages.door}
                content={doorWithCode ?? EMPTY_CELL_CONTENT}
              />
            )}
          />
          <InfoItem
            titleLabel={messages.billingInfo}
            addressText={formatOrderAddress(billingAddress)}
            company={billingAddress?.company}
            phoneNumber={billingAddress?.phone}
            renderRightInfo={() => (
              <>
                <DetailsListItem
                  title={messages.billingCode}
                  content={orderBillingCode ?? EMPTY_CELL_CONTENT}
                />
                <DetailsListItem
                  title={messages.paymentMethod}
                  content={paymentMethodName ?? EMPTY_CELL_CONTENT}
                />
              </>
            )}
            renderLeftInfo={() => (
              <DetailsListItem
                title={messages.getTaxLabel(orderTaxLabel)}
                content={orderTaxNumber ?? EMPTY_CELL_CONTENT}
              />
            )}
          />
        </>
      )}
    </Container>
  )
}

const InfoItem = ({
  titleLabel,
  addressText,
  company,
  phoneNumber,
  email,
  renderRightInfo,
  renderLeftInfo,
}: {
  titleLabel: string
  addressText: string
  company?: Maybe<string>
  phoneNumber?: Maybe<string>
  email?: Maybe<string>
  renderRightInfo?: () => React.ReactNode
  renderLeftInfo?: () => React.ReactNode
}) => {
  const rightDetails = renderRightInfo?.() ?? null
  const leftDetails = renderLeftInfo?.() ?? null

  return (
    <ItemContainer>
      <Text
        color="primary-400"
        fontWeight="semibold"
        fontSize="small"
        letterSpacing="tighter"
      >
        {titleLabel}
      </Text>
      <InfoRow>
        <LeftInfo>
          <AddressName className="notranslate">
            {company ?? EMPTY_CELL_CONTENT}
          </AddressName>
          <Address className="notranslate">
            <AddressText>{addressText}</AddressText>
            <AddressText>{phoneNumber ?? EMPTY_CELL_CONTENT}</AddressText>
            {!!email && <AddressText>{email}</AddressText>}
          </Address>
          {leftDetails}
        </LeftInfo>
        <RightInfo>{rightDetails}</RightInfo>
      </InfoRow>
    </ItemContainer>
  )
}

const InfoItemSkeleton = () => (
  <ItemContainer>
    <LoadingSkeleton width={'20%'} height={8} />
    <InfoRow>
      <LeftInfo>
        <LoadingSkeleton width={'20%'} height={8} />
        <Address>
          <LoadingSkeleton width={'80%'} height={10} />
          <LoadingSkeleton width={'28%'} height={10} />
          <LoadingSkeleton width={'50%'} height={10} />
        </Address>
      </LeftInfo>
      <RightInfo>
        <div>
          <LoadingSkeleton width={'60%'} height={8} />
          <LoadingSkeleton width={'40%'} height={10} />
        </div>
        <div>
          <LoadingSkeleton width={'70%'} height={8} />
          <LoadingSkeleton width={'50%'} height={10} />
        </div>
      </RightInfo>
    </InfoRow>
  </ItemContainer>
)

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (min-width: 1920px) {
    flex-direction: row;
    justify-content: space-between;
  }
`

const ItemContainer = styled.div`
  @media (min-width: 1920px) {
    width: 50%;
    padding-right: 120px;
  }
`

const InfoRow = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 1366px) {
    flex-direction: row;
  }

  @media (min-width: 1920px) {
    flex-direction: column;
  }
`

const LeftInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 3;
  gap: 4px;
`

const AddressName = styled.p`
  ${typography.alpha.primaryActive.body2}
  margin: 0 !important;
`

const Address = styled.div`
  display: flex;
  flex-direction: column;
`

const AddressText = styled.p`
  ${typography.alpha.primaryActive.subtitle3}
  margin: 0 !important;
  word-wrap: break-word;
`

const RightInfo = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
`
